import type { ResumeCardResponse } from './apiContracts';
import type { ResumeCardFetcher } from './apiClientResponses';

type ResumeCardInflight = { key: string; promise: Promise<ResumeCardResponse>; settledAt: number | null } | null;

export class ResumeCardRequestCoalescer {
	private inflight: ResumeCardInflight = null;

	get(limit: number | undefined, fetcher: ResumeCardFetcher, signal?: AbortSignal) {
		const q = limit && limit > 1 ? `?limit=${limit}` : '';
		const key = q;
		const now = Date.now();
		if (this.inflight && this.inflight.key === key && (this.inflight.settledAt === null || now - this.inflight.settledAt < 2_000)) {
			return waitForConsumer(this.inflight.promise, signal);
		}
		const promise = fetcher(`/api/user/progress/recent${q}`);
		const request = { key, promise, settledAt: null as number | null };
		this.inflight = request;
		// Drop the cache once the request settles — never serve stale data
		// beyond the 2s coalescing window or after a known mutation.
		const retainForFreshnessWindow = () => {
			request.settledAt = Date.now();
			if (this.inflight?.promise === promise) {
				setTimeout(() => {
					if (this.inflight?.promise === promise) this.inflight = null;
				}, 2_000);
			}
		};
		const clearRejectedRequest = () => {
			if (this.inflight?.promise === promise) this.inflight = null;
		};
		void promise.then(retainForFreshnessWindow, clearRejectedRequest);
		return waitForConsumer(promise, signal);
	}

	invalidate(): void {
		this.inflight = null;
	}
}

function waitForConsumer<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
	if (!signal) return promise;
	if (signal.aborted) return Promise.reject(abortError());
	return new Promise<T>((resolve, reject) => {
		const onAbort = () => reject(abortError());
		signal.addEventListener('abort', onAbort, { once: true });
		promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
	});
}

function abortError(): DOMException {
	return new DOMException('The request was aborted.', 'AbortError');
}
